import CaseActivityPreviewModal from "@/components/cases/case-activity-preview-modal";
import CaseHearingModal from "@/components/cases/case-hearing-modal";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import { FileLinkWithViewer } from "@/components/core/file-link-with-viewer";
import { Form } from "@/components/core/forms/form.tsx";
import { useModal } from "@/components/core/modals/use-modal";
import { caseStages } from "@/constants/case-stages";
import { userPermissions } from "@/constants/user-permissions";
import { userRoles } from "@/constants/user-roles";
import {
	useCaseActivityScheduleHearingMutation,
	useCaseActivityStatusMutation,
	useCaseActivityTitleMutation,
} from "@/features/case-evaluation/api/queries/acitivities-query";
import {
	useCaseActivityApprovalMutation,
	useCaseActivityDeleteMutation,
	useCaseActivityDeleteReasonMutation,
	useCaseActivityNotifyMutation,
} from "@/features/case-evaluation/api/queries/acitivities-query";
import { caseEvidenceListAllQueryOptions } from "@/features/case-evaluation/api/queries/evidence-query";
import CaseActivityCancelHearingModal from "@/features/case-evaluation/components/case-acitivity-cancel-hearing-modal";
import CaseActivityExpungeModal from "@/features/case-evaluation/components/case-acitivity-expunge-modal";
import CaseActivityRescheduleHearingModal from "@/features/case-evaluation/components/case-acitivity-reschedule-hearing-modal";
import CaseActivityEditModal from "@/features/case-evaluation/components/case-activity-edit-modal";
import CaseActivityHearingMoveStageModal from "@/features/case-evaluation/components/case-activity-hearing-move-stage";
import CaseActivityModal from "@/features/case-evaluation/components/case-activity-modal";
import CaseActivityProofModal from "@/features/case-evaluation/components/case-activity-proof-modal";
import CaseActivityRemarksModal from "@/features/case-evaluation/components/case-activity-remarks-modal";
import CaseActivityShareModal from "@/features/case-evaluation/components/case-activity-share-modal";
import CaseActivityUpdateMediationModal from "@/features/case-evaluation/components/case-activity-update-mediation-modal";
import CaseActivityUpdateModal from "@/features/case-evaluation/components/case-activity-update-modal";
import CaseActivityViewRequestModal from "@/features/case-evaluation/components/case-activity-view-request-modal";
import CaseAppealModal from "@/features/case-evaluation/components/case-appeal-modal";
import CaseGrantModal from "@/features/case-evaluation/components/case-grant-modal";
import CasePaymentViewModal from "@/features/case-evaluation/components/case-payment-view-modal";
import ViewAmmendmentModal from "@/features/case-evaluation/components/view-ammendment-modal";
import { useCaseRecordStatusMutation } from "@/features/case-record/api/queries/case-records-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import userService from "@/features/dashboard/api/services/user-service";
import { CaseBlock, CaseBlockBody, CaseBlockHead } from "@/features/new-case-ui/case-layout";
import { HasPermission, HasRole, useAuth } from "@/hooks/use-auth";
import { queryClient } from "@/query-client";
import { formatDate } from "@/utils/format-date";
import {
	IconArrowBack,
	IconCalendarCheck,
	IconChevronDown,
	IconChevronRight,
	IconCircleDot,
	IconDatabaseExclamation,
	IconDots,
	IconMessageDots,
	IconPencilQuestion,
	IconPlus,
	IconScale,
	IconShare3,
	IconSortAscending,
	IconSortDescending,
} from "@tabler/icons-react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import moment from "moment";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import DataGrid, { type ColumnDef } from "../core/data-grid";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../core/hover-card";
import { HearingEvidenceFileViewer } from "../document-viewer/hearing-evidence-file-viewer";
import CaseActivityAddFileModal from "./case-activity-add-file-modal";
import CaseActivityCommentsModal from "./case-activity-comments-modal";
import CaseActivityDeleteModal from "./case-activity-delete-modal";
import CaseActivityNoteModal from "./case-activity-note-modal";
import CaseActivityNotifyModal from "./case-activity-notify-modal";
import CaseSheriffReportModal from "./case-sheriff-report-modal";

const MAX_DEPTH = 1;

interface CaseActivitiesProps {
	activities: CaseActivity;
	activeStatus: string;
	currentTab: string;
	nextVisibleStage: object;
	isReadOnly: boolean;
	data: CaseRecord;
	sortBy?: "latest" | "oldest";
	onSortChange?: (sortBy: "latest" | "oldest") => void;
}

const columns: ColumnDef<CaseActivity>[] = [
	{
		id: "title",
		label: "Title",
		cell: ({ data }) => <span>{data?.activity || "N/A"}</span>,
	},
	{
		id: "expunged_at",
		label: "Date Expunged",
		cell: ({ data }) => <span>{formatDate(data?.expunged_at)}</span>,
	},
	{
		id: "expunged_by",
		label: "Expunged By",
		cell: ({ data }) => <span>{data?.expunged_by}</span>,
	},
];

function ExpungeInfoCard({
	data,
}: {
	data: CaseActivity;
}) {
	const form = useForm({
		defaultValues: {
			id: 1,
		},
	});
	return (
		<Form {...form}>
			<form className="w-full">
				<div className="flex flex-col w-full p-4 gap-2">
					<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
						<DataGrid variant="compact" data={data} columns={columns} />
					</div>
				</div>
			</form>
		</Form>
	);
}

const CaseActivityItem = ({
	isReadOnly = false,
	title,
	depth = 0,
	activeStatus,
	activity = {},
	caseData = {},
	items = [],
	nextStage,
}: { title: string; depth?: number; isReadOnly: boolean }) => {
	const { hasRole } = useAuth();
	const [showReplies, setShowReplies] = useState(true);
	const [error, setError] = useState(null);
	const hasChildren = depth < MAX_DEPTH;
	const isExpunged = activity.status === "Expunged" || activity.status === "Denied";
	const isGranted = activity.status === "Granted";
	const isDraft = activity.state === "draft";
	const isCancelled = activity.hearing_status === "Cancelled" || activity.hearing_status === "Rescheduled";
	const modal = useModal();
	const navigate = useNavigate();
	const caseActivityApprovalMutation = useCaseActivityApprovalMutation();
	const caseActivityDeleteMutation = useCaseActivityDeleteMutation();
	const caseActivityDeleteReasonMutation = useCaseActivityDeleteReasonMutation();
	const caseActivityNotifyMutation = useCaseActivityNotifyMutation();

	const openEditModal = (activity) => {
		modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: (
				<CaseActivityEditModal
					title="Update Activity"
					collaborators={caseData.collaborators}
					data={activity}
					caseRecord={caseData}
					nextStage={nextStage}
					allowPostAs={caseData.allow_post_as}
					nextVisibleStage={undefined}
					moveTo={false}
					decision={false}
					isSheriffReport={false}
				/>
			),
		});
	};

	const openAddModal = (activityId = null, defaultActivity = null) => {
		const input = {
			id: activity.id,
			stage: activeStatus,
			activity_id: activityId || null,
			activity: defaultActivity,
		};
		modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: (
				<CaseActivityModal
					title="Add Activity"
					data={input}
					caseRecord={caseData}
					isSheriffReport={defaultActivity === "Sheriff's Report / Process Server's Report"}
				/>
			),
		});
	};

	const openFeeModal = (activity) => {
		modal.openModal({
			allowClose: true,
			title: "Case Payment Information",
			size: "sm",
			showHeader: true,
			showFooter: false,
			component: <CasePaymentViewModal data={activity} />,
		});
	};

	const caseActivitiesMutation = useCaseActivityStatusMutation();
	const caseActivitiesTitleMutation = useCaseActivityTitleMutation();
	const openExpungeModal = (activity = null, status = null, activityId = null) => {
		const modalId = modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: (
				<CaseActivityExpungeModal
					caseRecord={caseData}
					data={activity}
					title={status}
					handleSubmit={async (values) => {
						try {
							await caseActivitiesMutation.mutateAsync(values);
							modal.closeModal(modalId);
						} catch (error) {}
					}}
				/>
			),
		});
	};

	const openCancelHearingModal = (status = "Hearing") => {
		modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <CaseActivityCancelHearingModal title={status} data={activity} />,
		});
	};
	const openRescheduleHearingModal = (status = "Hearing") => {
		modal.openModal({
			allowClose: false,
			size: "2xl",
			showHeader: false,
			showFooter: false,
			component: <CaseActivityRescheduleHearingModal caseRecord={caseData} title={status} data={activity} />,
		});
	};

	const openMoveStageHearingModal = (status = "Hearing") => {
		modal.openModal({
			allowClose: false,
			size: "lg",
			showHeader: false,
			showFooter: false,
			component: <CaseActivityHearingMoveStageModal title={status} data={activity} />,
		});
	};

	const openSheriffModal = (activity) => {
		modal.openModal({
			allowClose: false,
			size: "xl",
			showHeader: false,
			showFooter: false,
			component: <CaseSheriffReportModal caseRecord={caseData} activity={activity} collaborators={caseData.collaborators} />,
		});
	};
	const openAddNoteModal = (activity) => {
		modal.openModal({
			allowClose: true,
			size: "xl",
			showHeader: false,
			showFooter: false,
			component: <CaseActivityNoteModal caseRecord={caseData} activity={activity} branchId={caseData.branch_id} />,
		});
	};

	const openAddFileModal = (activity) => {
		modal.openModal({
			allowClose: false,
			size: "md",
			title: "Add File",
			showFooter: false,
			component: <CaseActivityAddFileModal caseRecord={caseData} activity={activity} />,
		});
	};

	const openActivityEditModal = (data) => {
		modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component:
				data.is_order === true ? (
					<CaseActivityUpdateModal caseRecord={caseData} data={data} showEditor={data.type === "Hearing"} />
				) : (
					<CaseActivityRemarksModal data={data} />
				),
		});
	};

	const openShareModal = (activity) => {
		modal.openModal({
			size: "md",
			title: "Share Activity",
			description: "Share this activity to other users",
			showFooter: false,
			component: <CaseActivityShareModal activity={activity} branchId={caseData.branch_id} />,
		});
	};
	const openViewRequestModal = (activity) => {
		modal.openModal({
			size: "lg",
			title: "View Request",
			description: `Hearing: ${activity?.hearingParent?.remarks}`,
			showFooter: false,
			component: <CaseActivityViewRequestModal activity={activity} />,
		});
	};

	const openUploadProofModal = (data) => {
		modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <CaseActivityProofModal caseRecord={caseData} data={data} showEditor={false} />,
		});
	};

	const openActivityEditMediationModal = (data) => {
		modal.openModal({
			allowClose: false,
			size: data.type === "Hearing" || data.is_order === true ? "full" : "md",
			showHeader: false,
			showFooter: false,
			component: <CaseActivityUpdateMediationModal caseRecord={caseData} data={data} showEditor={data.type === "Hearing" || data.is_order === true} />,
		});
	};

	const removeModal = (data) => {
		modal.openModal({
			title: `Remove ${data.activity}`,
			description: "You are removing this activity. Please confirm.",
			type: "confirm",
			variant: "green",
			onConfirm: async () => {
				await caseActivityDeleteReasonMutation.mutateAsync(data.id);
			},
		});
	};

	const openApprovalModal = (data) => {
		modal.openModal({
			title: "Approve",
			description: "You are approving this activity. Please confirm.",
			type: "confirm",
			variant: "green",
			onConfirm: async () => {
				await caseActivityApprovalMutation.mutateAsync({
					id: data.id,
					status: "approved",
				});
			},
		});
	};

	const openGrantModal = (activity = null, status = null, activityId = null) => {
		const modalId = modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: (
				<CaseGrantModal
					caseRecord={caseData}
					data={activity}
					title={status}
					caseId={activity.id}
					nextStage={nextStage}
					activityId={activityId}
					handleSubmit={async (values) => {
						try {
							await caseActivitiesMutation.mutateAsync(values);
							await queryClient.invalidateQueries({
								queryKey: ["case_activities"],
							});
							await queryClient.invalidateQueries({
								queryKey: ["case_records"],
							});
							await queryClient.invalidateQueries({
								queryKey: ["case_counsels"],
							});
							if (values.move_to_next_stage === true) {
								await navigate({
									to: `/case-evaluation/${caseData.id}`,
									search: (old) => ({
										...old,
										tabs: "case-flow",
										stage: values?.next_stage,
									}),
								});
							}
							modal.closeModal(modalId);
						} catch (error) {}
					}}
				/>
			),
		});
	};

	const openPreviewModal = (data) => {
		modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <CaseActivityPreviewModal title={data.activity} data={data} />,
		});
	};

	const openViewAmendmentModal = (data) => {
		modal.openModal({
			allowClose: false,
			size: "xl",
			showHeader: false,
			showFooter: false,
			component: <ViewAmmendmentModal data={data} />,
		});
	};
	const [evidenceID, setEvidenceId] = useState<string | undefined>();

	const dataQuery = useQuery(
		caseEvidenceListAllQueryOptions(
			{
				id: evidenceID,
			},
			{
				enabled: !!evidenceID,
			},
		),
	);
	const evidenceData = dataQuery.data?.data;

	const openEvidenceViewer = (evidenceId: string, index?: number, caseRecordUserId?: string) => {
		modal.openModal({
			title: "File Preview",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: (
				<HearingEvidenceFileViewer
					evidenceId={evidenceId}
					caseId={activity.case_id}
					caseActivityId={activity.id}
					index={index}
					caseRecordUserId={evidenceData?.[0]?.case_record_user_id || caseRecordUserId}
				/>
			),
		});
	};

	const openActivityCommentsModal = (data) => {
		modal.openModal({
			allowClose: true,
			size: "xl",
			showHeader: false,
			showFooter: false,
			component: <CaseActivityCommentsModal title="Activity Comments" data={data} />,
		});
	};

	return (
		<div className="flex flex-col w-full border-b last:border-b-0 border-dashed border-border overflow-hidden">
			<CaseBlock showContent={true}>
				<div className="flex flex-col w-full">
					{/* {activity?.with_fees === true && activity?.status === null && (
					<div className="flex flex-col w-full">
						<div className="flex flex-row items-center w-full gap-2">
							<Alert
								type="error"
								className="!rounded-none"
								allowClose={false}
								title="Only you can see this post at the moment!"
								icon={<IconAlertCircle size={20} stroke={2} />}
							>
								Waiting for OCC payment confirmation. This will not be displayed to the Court and other Party until payment is Confirmed.
							</Alert>
						</div>
					</div>
				)} */}
					<div className="flex flex-row items-center w-auto p-4 gap-2">
						<div
							className={clsx(
								"inline-flex shrink-0 self-start justify-center items-center w-[32px] h-[32px] rounded-full text-primary-foreground",
								isDraft && "bg-gray-500",
								activity.receipient === "Bcc" || activity.receipient === "Judge"
									? "bg-primary-500"
									: activity.receipient === "Defendant"
										? "bg-red-500"
										: "bg-blue-500",
							)}
						>
							{/* {activity.receipient === "Bcc" || activity.receipient === "Judge" ? <IconScale size={18} stroke={2} /> : activity.receipient?.charAt(0)}{" "} */}
							<div>
								{activity.type === "Hearing" ? (
									<IconCalendarCheck size={18} stroke={2} className="bg-teal-500 w-[32px] h-[32px] text-white p-1.5 rounded-full" />
								) : (activity.receipient === "Bcc" || activity.receipient === "Judge") && activity.type !== "Hearing" ? (
									<IconScale size={18} stroke={2} />
								) : (
									activity.receipient?.charAt(0)
								)}
							</div>
						</div>

						<div className="flex flex-row w-full gap-2">
							<div className="flex flex-col w-full">
								<div className="inline-flex flex-row items-center gap-1">
									{isExpunged && (
										<Badge className="uppercase" size="xs" color="red-light">
											<HoverCard openDelay={300} closeDelay={300}>
												<HoverCardTrigger asChild>
													<button type="button">{activity.status}</button>
												</HoverCardTrigger>
												<HoverCardContent>
													<ExpungeInfoCard data={activity} />
												</HoverCardContent>
											</HoverCard>
										</Badge>
									)}
									{isGranted && (
										<Badge className="uppercase" size="xs" color="green-light">
											{activity.status}
										</Badge>
									)}
									{activity.counter_claim && activity.counter_claim !== "none" && (
										<Badge className="uppercase" size="xs" color="red-light">
											{activity.counter_claim === "compulsory"
												? "with compulsory counter claim"
												: activity.counter_claim === "permissive"
													? "with permissive counter claim"
													: activity.counter_claim === "both"
														? "with compulsory and permissive counter claim"
														: activity.counter_claim}
										</Badge>
									)}
									{isCancelled && (
										<Badge className="uppercase" size="xs" color="red-light">
											{activity.hearing_status}
										</Badge>
									)}
									{!isDraft && activity.service_status && (
										<Badge className="uppercase" size="xs" color="green-light">
											{activity.service_status}
										</Badge>
									)}
									{activity.activity !== "Case Closed" && activity.type === "Court Actions" && activity.party_type === "Bcc" && !activity.approved_at && (
										<Badge className="uppercase" size="xs" color="red-light">
											{activity.approved_at ? "Approved" : "Pending Approval"}
										</Badge>
									)}
								</div>
								<div className="inline-flex flex-row items-center justify-between gap-1">
									<button
										type="button"
										onClick={() => {
											activity.tag === "Sheriff's Report" && activity.sheriffsReport?.length !== 0 ? openPreviewModal(activity) : openPreviewModal(activity);
											// activity.tag === "Sheriff's Report" && activity.sheriffsReport?.length !== 0 ? openSheriffModal(activity) : openPreviewModal(activity);
										}}
										className={
											activity.status !== "Expunged" && activity.hearing_status !== "Cancelled" && activity.hearing_status !== "Rescheduled"
												? "font-semibold text-base text-foreground-dark max-w-[700px] truncate"
												: "font-semibold text-base text-foreground-dark line-through"
										}
									>
										{isDraft && "Draft - "} {title} {activity?.mode_of_dismissal && `(${activity.mode_of_dismissal})`}
									</button>
								</div>
								<div className="text-base text-foreground/70">
									<div>
										{activity?.case_activity_users?.map((user, index) => (
											<div key={index} className=" items-center space-x-2 mr-1">
												{user.defendant?.first_name || user.defendant?.user?.full_name} {user.defendant?.last_name}
											</div>
										))}
									</div>
									{/* {activity?.with_fees === true && (
									<button
										type="button"
										className="cursor-pointer font-semibold"
										onClick={() => {
											openFeeModal(activity);
										}}
									>
										- with fees
									</button>
								)} */}
								</div>
								<div className="flex flex-col w-auto">
									{!isDraft && activity.activity === "Hearing" && (
										<div className="inline-flex flex-row items-start gap-1">
											<span className="text-base text-foreground/70">Activity/Event:</span>
											<span className="text-base text-foreground-dark">{activity.remarks}</span>
										</div>
									)}
									{!isDraft && activity.due_at && (
										<div className="inline-flex flex-row items-start gap-1">
											<span className="text-base text-foreground/70">{activity.activity === "Hearing" ? "Scheduled" : "Due"}</span>
											<span className="text-base text-foreground-dark">
												{formatDate(activity.due_at)}
												{activity.hearing_mode && ` - ${activity.hearing_mode || ""}`}
											</span>
										</div>
									)}
									<div className="flex flex-col gap-1">
										{!isDraft && activity.activity === "Hearing" && activity?.exhibits?.length !== 0 && (
											<div className="flex flex-row gap-1 ">
												<div className="text-base text-foreground/70 pt-1">{activity.activity === "Hearing" && "Exhibits"}</div>
												<div className="flex gap-2">
													{activity?.exhibits?.length !== null &&
														activity?.exhibits?.slice(0, 3).map((exhibit: { label: string; value: string; case_record_user_id: string }, index: number) => (
															<button
																key={index}
																onClick={() => {
																	setEvidenceId(exhibit.value);
																	openEvidenceViewer(exhibit.value, 1, exhibit?.case_record_user_id);
																}}
																type="button"
																className="cursor-pointer text-start max-w-[200px] p-1 rounded-md bg-blue-50 hover:bg-blue-100 h-[28px] px-2 text-sm tracking-tight text-blue-600 underline-offset-2 hover:underline font-semibold truncate"
															>
																{exhibit.label}
															</button>
														))}
													{activity?.exhibits?.length > 3 && (
														<button
															type="button"
															className="text-foreground/70 text-sm font-semibold hover:underline"
															onClick={() => {
																setEvidenceId(activity?.exhibits[0].value);
																openEvidenceViewer(activity?.exhibits[0].value);
															}}
														>
															+{activity?.exhibits?.length - 3} more
														</button>
													)}
												</div>
											</div>
										)}

										{!isDraft && activity.activity === "Hearing" && activity?.witness_name?.length !== 0 && (
											<div className="inline-flex flex-row items-center gap-1">
												<span className="text-base text-foreground/70">{activity.activity === "Hearing" && "Witness Name"}</span>
												{activity?.witness_name?.map((witness: { label: string; value: string; case_record_user_id: string }, index: number) => (
													<button
														//  variant={"ghost"}
														// size={"icon"}
														key={index}
														onClick={() => {
															setEvidenceId(witness.value);
															openEvidenceViewer(witness.value, 1, witness.case_record_user_id);
														}}
														type="button"
														className="cursor-pointer text-start p-1 rounded-md bg-blue-50 hover:bg-blue-100 h-[28px] px-2 text-sm tracking-tight text-blue-600 underline-offset-2 hover:underline font-semibold"
													>
														{witness.label}
													</button>
												))}
											</div>
										)}

										{activity?.sheriffsReport && activity?.sheriffsReport.length !== 0 && (
											<div className="text-base text-foreground/70">
												<div className="items-center space-x-2 mr-1">
													Report For:
													{activity?.sheriffsReport?.map((user, index) => (
														<strong key={index} className="ml-2">
															{user?.case_user?.full_name}
														</strong>
													))}
												</div>
											</div>
										)}
										{!isDraft && activity.attachments.length !== 0 && (
											<div>
												<span className="text-base text-foreground/70">{activity.activity === "Hearing" && "Proof of Payment"}</span>
												<div className="flex flex-row flex-wrap gap-1.5 my-2">
													{activity.attachments.map((file, index) => (
														<FileLinkWithViewer key={index} file={file} url={file.original_url} maxLength={20} mime_type={""} />
													))}
													{activity.proofs.map((file, index) => (
														<FileLinkWithViewer key={index} file={file} url={file.original_url} maxLength={20} mime_type={""} />
													))}
												</div>
											</div>
										)}
									</div>
									{!isDraft &&
										activity.ordered_at &&
										activity.type !== "Hearing" &&
										activity.party_type !== "Defendant" &&
										activity.party_type !== "Plaintiff" && (
											<div className="inline-flex flex-row items-start gap-1">
												<span className="text-base text-foreground/70">
													{activity.tag !== "Sheriff's Report" && activity.tag !== "Minutes" ? "Date Ordered" : "Date of Report"}
												</span>
												<span className="text-base text-foreground-dark">{formatDate(activity.ordered_at)}</span>
											</div>
										)}
									{!isDraft && activity.scheduled_at && activity.type !== "Hearing" && (
										<div className="inline-flex flex-row items-start gap-1">
											<span className="text-base text-foreground/70">Calendar Date</span>
											<span className="text-base text-foreground-dark">{formatDate(activity.scheduled_at)}</span>
										</div>
									)}
									{!isDraft && activity.service_date && activity.type !== "Hearing" && (
										<div className="inline-flex flex-row items-start gap-1">
											<span className="text-base text-foreground/70">Date Served</span>
											<span className="text-base text-foreground-dark">{formatDate(activity.service_date, "PP")}</span>
										</div>
									)}
									{!isDraft && activity.service_attempt_date && activity.type !== "Hearing" && (
										<div className="inline-flex flex-row items-start gap-1">
											<span className="text-base text-foreground/70">Date Attempt</span>
											<span className="text-base text-foreground-dark">{formatDate(activity.service_attempt_date, "PP")}</span>
										</div>
									)}
									{!isDraft && activity.submitted_at && (
										<div className="inline-flex flex-row items-start gap-1">
											<span className="text-base text-foreground/70">Date Submitted</span>
											<span className="text-base text-foreground-dark">{formatDate(activity.submitted_at)}</span>
										</div>
									)}
									{/* <div className="inline-flex flex-row items-start gap-1">
								<span className="text-base text-foreground/70">{activity.activity === "Hearing" ? "Activity/Event" : "Remarks"}: </span>
								<span className="text-base text-foreground-dark">{activity.remarks || "-"}</span>
							</div> */}

									{!isReadOnly && (
										<HasPermission permissions={[userPermissions.UPDATE_TSN]}>
											{!isDraft &&
												activity.status === "for_update" &&
												activity.activity === "Transcript of Stenographic Notes" &&
												!isExpunged &&
												!isReadOnly && (
													<div className="flex flex-row items-center w-full mt-2 gap-1">
														<Button
															type="button"
															size="sm"
															variant="ghost"
															// disabled={!caseData.jdr_id && activity.type === "ORDER_REFERRING_TO_JDR"}
															onClick={() => {
																activity.type === "Hearing"
																	? openActivityEditModal(activity)
																	: activity.is_order === false
																		? openActivityEditModal(activity)
																		: openActivityEditMediationModal(activity);
															}}
														>
															<IconPencilQuestion size={14} stroke={2} />
															<span className="">Update</span>
														</Button>
														{activity.activity === "Transcript of Stenographic Notes" && activity?.view_request?.length > 0 && (
															<Button
																type="button"
																size="sm"
																variant="green"
																onClick={() => {
																	openViewRequestModal(activity);
																}}
															>
																<span className="">View Request</span>
															</Button>
														)}
													</div>
												)}
										</HasPermission>
									)}

									<HasPermission permissions={[userPermissions.UPDATE_MOH]}>
										{!isDraft && !isReadOnly && activity.status === "for_update" && activity.activity === "Minutes of the Hearing" && !isExpunged && (
											<div className="flex flex-row items-center w-full mt-2 gap-1">
												<Button
													type="button"
													size="sm"
													variant="ghost"
													// disabled={!caseData.jdr_id && activity.type === "ORDER_REFERRING_TO_JDR"}
													onClick={() => {
														activity.type === "Hearing" || activity.tag === "Sheriff's Report"
															? openActivityEditModal(activity)
															: activity.is_order === false
																? openActivityEditModal(activity)
																: openActivityEditMediationModal(activity);
													}}
												>
													<IconPencilQuestion size={14} stroke={2} />
													<span className="">Update</span>
												</Button>
											</div>
										)}
									</HasPermission>
									<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
										{!isDraft &&
											!isReadOnly &&
											activity.status === "for_update" &&
											activity.activity !== "Transcript of Stenographic Notes" &&
											activity.activity !== "Minutes of the Hearing" &&
											!isExpunged && (
												<div className="flex flex-row items-center w-full mt-2 gap-1">
													<Button
														type="button"
														size="sm"
														variant="ghost"
														// disabled={!caseData.jdr_id && activity.type === "ORDER_REFERRING_TO_JDR"}
														onClick={() => {
															activity.type === "Hearing" || activity.tag === "Sheriff's Report" || activity.tag === "Minutes"
																? openActivityEditModal(activity)
																: activity.is_order === false
																	? openActivityEditModal(activity)
																	: openActivityEditMediationModal(activity);
														}}
													>
														<IconPencilQuestion size={14} stroke={2} />
														<span className="">Update</span>
													</Button>
												</div>
											)}

										{isDraft && !isReadOnly && activity.type !== "Hearing" && (
											<div className="flex flex-row items-center w-full mt-2 gap-1">
												<Button
													type="button"
													size="sm"
													variant={"ghost"}
													onClick={() => {
														openEditModal(activity);
													}}
												>
													<IconPencilQuestion size={14} stroke={2} />
													<span className="">Continue Edit</span>
												</Button>
												<HasRole roles={[userRoles.JUDGE]}>
													<Button
														type="button"
														size="sm"
														variant={"ghost"}
														onClick={() => {
															openShareModal(activity);
														}}
													>
														<IconShare3 size={14} stroke={2} />
														<span className="">Share To</span>
													</Button>
												</HasRole>
											</div>
										)}
									</HasPermission>

									<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
										{activity.proofs?.length === 0 && activity?.with_fees === true && !isReadOnly && activity?.status === null && !isExpunged && (
											<div className="flex flex-row items-center w-full mt-2 gap-1">
												<Button
													type="button"
													variant="green"
													size="sm"
													onClick={() => {
														openUploadProofModal(activity);
													}}
												>
													Upload Proof of Payment
												</Button>
											</div>
										)}

										{activity?.with_grant === true && !isReadOnly && activity?.status === null && !isExpunged && (
											<div className="flex flex-row items-center w-full mt-2 gap-1">
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => {
														openGrantModal(activity, "Granted", activity.id);
													}}
												>
													Grant
												</Button>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => {
														openGrantModal(activity, "Denied", activity.id);
													}}
												>
													Deny
												</Button>
											</div>
										)}
										{!isExpunged &&
											!isReadOnly &&
											activity.tag !== "Sheriff's Report" &&
											activity.party_type === "Bcc" &&
											activity.type !== "MEDIATION_REPORT" &&
											activity.activity !== "Judicial Dispute Resolution Report" &&
											activity.type !== "Hearing" &&
											hasRole([userRoles.JUDGE]) &&
											!activity.approved_at && (
												<div className="flex flex-row items-center w-full mt-2 gap-1">
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => {
															openApprovalModal(activity);
														}}
													>
														Approve
													</Button>
												</div>
											)}
									</HasPermission>
								</div>
								{/* {items.length > 0 && (
							<div className="flex flex-row w-full mt-2">
								<button
									className="inline-flex flex-row items-center gap-1 text-base text-foreground/70 hover:text-foreground"
									type="button"
									onClick={() => setShowReplies((prev) => !prev)}
								>
									<IconArrowBack size={14} stroke={2} />
									<span>{showReplies ? `Hide ${items.length} Replies` : `Show ${items.length} Replies`}</span>
								</button>
							</div>
						)} */}
								{/* {items.length > 0 && showReplies && depth < MAX_DEPTH && (
              <div className="flex flex-col w-full mt-3 rounded-md border border-border/50">
                {items.map((itemActivity, index) => (
                  <CaseActivityChild
                    key={index}
                    activeStatus={activeStatus}
                    title={itemActivity.activity}
                    depth={depth + 1}
                    activity={itemActivity}
                    caseData={caseData}
                    nextStage={nextStage}
                  />
                ))}
              </div>
            )} */}
							</div>

							<div className="inline-flex self-start shrink-0 flex-row w-auto ml-auto items-center gap-2">
								{!isDraft && (
									<span className="text-base text-foreground/70 shrink-0">
										{activity.filed_at && `${activity.author?.full_name} Filed on ${formatDate(activity.filed_at, "PPp")}`}
									</span>
								)}
								<Button
									className="!rounded-full relative"
									type="button"
									size="icon"
									variant="ghost"
									onClick={() => {
										openAddNoteModal(activity);
									}}
								>
									{/* {unreadNote && ( */}
									{/* <div className="absolute -top-1 -right-1 inline-flex shrink-0 justify-center items-center h-[12px] w-[12px] rounded-full bg-red-600 border-[2px] border-background pointer-events-none" /> */}
									{/* )} */}
									<IconMessageDots size={14} stroke={2} />
								</Button>
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button className="!rounded-full" type="button" size="icon" variant="ghost">
											<IconDots size={14} stroke={2} />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										<DropdownMenuGroup>
											{activity.state === "draft" && !isReadOnly && (
												<DropdownMenuItem
													onClick={() => {
														openEditModal(activity);
													}}
													className="cursor-pointer"
												>
													<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
														<span>Edit</span>
													</div>
												</DropdownMenuItem>
											)}

											{activity.activity !== "Hearing" &&
												(activity.tag === "Sheriff's Report" ? (
													<DropdownMenuItem
														onClick={() => {
															openPreviewModal(activity);
															// openSheriffModal(activity);
														}}
														className="cursor-pointer"
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>View</span>
														</div>
													</DropdownMenuItem>
												) : (
													<DropdownMenuItem
														onClick={() => {
															if (activity.is_amendment) {
																openViewAmendmentModal(activity);
															} else {
																openPreviewModal(activity);
															}
														}}
														className="cursor-pointer"
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>View</span>
														</div>
													</DropdownMenuItem>
												))}
											{/* <HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
											{depth < MAX_DEPTH &&
												activeStatus === activity.stage &&
												activity.status !== "Sheriff's Report" &&
												activity.status !== "Expunged" &&
												activity.activity !== "Hearing" && (
													<DropdownMenuItem
														onClick={() => {
															openAddModal(activity.id);
														}}
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
															<span className="inline-flex">Add Activity</span>
														</div>
													</DropdownMenuItem>
												)}
										</HasPermission> */}
											<HasPermission permissions={[userPermissions.ADD_SHERIFFS_REPORT]}>
												{!isExpunged &&
													!isReadOnly &&
													depth < MAX_DEPTH &&
													activity.status !== "Sheriff's Report" &&
													activity.status !== "Expunged" &&
													activity.activity !== "Hearing" && (
														<DropdownMenuItem
															onClick={() => {
																openAddModal(activity.id, "Sheriff's Report / Process Server's Report");
															}}
														>
															<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
																<span className="inline-flex">Add Sheriff's Report</span>
															</div>
														</DropdownMenuItem>
													)}
											</HasPermission>

											<HasPermission permissions={[userPermissions.EXPUNGE_ACTIVITY]}>
												{!isExpunged && !isReadOnly && (
													<DropdownMenuItem
														onClick={() => {
															openExpungeModal(activity, "Expunged", activity.id);
														}}
														className="cursor-pointer"
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span className="inline-flex">Expunge</span>
														</div>
													</DropdownMenuItem>
												)}
											</HasPermission>

											{/* <HasPermission permissions={[userPermissions.UPDATE_HEARING]}>
											{!isExpunged &&
												!isReadOnly &&
												activity.status === "for_update" &&
												(activity.activity === "Sheriff's Report" ||
													activity.activity === "Subpoena" ||
													activity.activity === "Transcript of Stenographic Notes" ||
													activity.activity === "Minutes of the Hearing") && (
													<DropdownMenuItem
														onClick={() => {
															removeModal(activity);
														}}
														className="cursor-pointer"
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>Remove</span>
														</div>
													</DropdownMenuItem>
												)}
										</HasPermission> */}
											{!isReadOnly && (
												<HasPermission permissions={[userPermissions.UPDATE_TSN]}>
													{!isDraft && activity.activity === "Transcript of Stenographic Notes" && !isExpunged && !isReadOnly && (
														<DropdownMenuItem
															onClick={() => {
																activity.type === "Hearing" || activity.tag === "Sheriff's Report" || activity.tag === "Minutes"
																	? openActivityEditModal(activity)
																	: activity.is_order === false
																		? openActivityEditModal(activity)
																		: openActivityEditMediationModal(activity);
															}}
														>
															<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																<span>Update</span>
															</div>
														</DropdownMenuItem>
													)}
												</HasPermission>
											)}
											{!isReadOnly && (
												<HasPermission permissions={[userPermissions.UPDATE_MOH]}>
													{!isDraft && activity.activity === "Minutes of the Hearing" && !isExpunged && !isReadOnly && (
														<DropdownMenuItem
															onClick={() => {
																activity.type === "Hearing" || activity.tag === "Sheriff's Report"
																	? openActivityEditModal(activity)
																	: activity.is_order === false
																		? openActivityEditModal(activity)
																		: openActivityEditMediationModal(activity);
															}}
														>
															<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																<span>Update</span>
															</div>
														</DropdownMenuItem>
													)}
												</HasPermission>
											)}

											{/* <HasPermission permissions={[userPermissions.UPDATE_TSN]}>
											{!isExpunged &&
												!isReadOnly &&
												activity.hearing_status !== "Cancelled" &&
												activity.hearing_status !== "Rescheduled" &&
												activity.activity === "Hearing" && (
													<DropdownMenuItem
														onClick={() => {
															openAddModal(activity.id, "Transcript of Stenographic Notes");
														}}
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>Add Transcript of Stenographic Notes</span>
														</div>
													</DropdownMenuItem>
												)}
										</HasPermission> */}
											{/* <HasPermission permissions={[userPermissions.UPDATE_MOH]}>
											{!isExpunged &&
												!isReadOnly &&
												activity.hearing_status !== "Cancelled" &&
												activity.hearing_status !== "Rescheduled" &&
												activity.activity === "Hearing" && (
													<DropdownMenuItem
														onClick={() => {
															openAddModal(activity.id, "Minutes of the Hearing");
														}}
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>Add Minutes of Hearing</span>
														</div>
													</DropdownMenuItem>
												)}
										</HasPermission> */}
											{(activity.party_type === "Bcc" || caseData.allow_post_as) &&
												(activity.type !== "Hearing" ||
													activity.tag === "Sheriff's Report" ||
													activity.activity === "Subpoena" ||
													activity.activity === "Order/Constancia") && (
													<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
														{activity.tag !== "Sheriff's Report" && activity.activity !== "Subpoena" && activity.activity !== "Order/Constancia" ? (
															<DropdownMenuItem
																onClick={() => {
																	activity.type === "MEDIATION_REPORT"
																		? openActivityEditMediationModal(activity)
																		: activity.type === "JDR"
																			? openActivityEditModal(activity)
																			: openEditModal(activity);
																}}
															>
																<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																	<span>Update</span>
																</div>
															</DropdownMenuItem>
														) : activity.tag === "Sheriff's Report" ? (
															<>
																<DropdownMenuItem
																	onClick={() => {
																		openAddFileModal(activity);
																	}}
																>
																	<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																		<span>Add File</span>
																	</div>
																</DropdownMenuItem>
																<DropdownMenuItem
																	onClick={() => {
																		openActivityEditModal(activity);
																		// openSheriffModal(activity);
																	}}
																>
																	<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																		<span>Update</span>
																	</div>
																</DropdownMenuItem>
															</>
														) : (
															<DropdownMenuItem
																onClick={() => {
																	openActivityEditModal(activity);
																}}
															>
																<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																	<span>Update</span>
																</div>
															</DropdownMenuItem>
														)}
													</HasPermission>
												)}

											<HasPermission permissions={[userPermissions.UPDATE_HEARING]}>
												{!isExpunged &&
													!isReadOnly &&
													activity.hearing_status !== "Cancelled" &&
													activity.hearing_status !== "Rescheduled" &&
													activity.activity === "Hearing" && (
														<DropdownMenuItem
															onClick={() => {
																openRescheduleHearingModal();
															}}
														>
															<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																<span>Reschedule Hearing</span>
															</div>
														</DropdownMenuItem>
													)}
											</HasPermission>

											{activity.activity === "Referral to Mediation" && !isReadOnly && (
												<DropdownMenuItem
													onClick={() => {
														openUpdateStatusModal();
													}}
												>
													<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
														<IconArrowBack size={16} stroke={2} />
														<span>Update status</span>
													</div>
												</DropdownMenuItem>
											)}

											<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
												<DropdownMenuItem
													onClick={() => {
														const modalId = modal.openModal({
															allowClose: true,
															title: "Notify Parties",
															size: "md",
															showHeader: true,
															showFooter: false,
															component: (
																<CaseActivityNotifyModal
																	data={activity}
																	handleSubmit={async (values) => {
																		const response = await userService.validatePassword(values.password);
																		if (response.message === "Valid") {
																			setError(null);
																			sessionStorage.setItem("password_entered_at", moment());
																			await caseActivityNotifyMutation.mutateAsync(values);

																			modal.closeModal(modalId);
																		} else {
																			setError(response.message);
																			toast.error("Invalid password. Please try again.");
																		}
																	}}
																/>
															),
														});
													}}
												>
													<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
														<span>Notify Parties</span>
													</div>
												</DropdownMenuItem>
											</HasPermission>

											<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
												<DropdownMenuItem
													onClick={() => {
														const modalId = modal.openModal({
															allowClose: true,
															title: "Delete Case Activity",
															size: "md",
															showHeader: true,
															showFooter: false,
															component: (
																<CaseActivityDeleteModal
																	data={activity}
																	handleSubmit={async (values) => {
																		const response = await userService.validatePassword(values.password);
																		if (response.message === "Valid") {
																			setError(null);
																			sessionStorage.setItem("password_entered_at", moment());
																			await caseActivityDeleteReasonMutation.mutateAsync(values);
																			await navigate({
																				to: `/case-evaluation/${caseData.id}`,
																				search: (old) => ({
																					...old,
																					tabs: "case-flow",
																					stage: activity.move_to_stage === caseStages.JUDGMENT ? caseStages.TRIAL_PROPER : activity.stage,
																				}),
																				replace: true,
																			});

																			await queryClient.invalidateQueries({
																				queryKey: ["case_activities"],
																			});
																			await queryClient.invalidateQueries({
																				queryKey: ["case_records"],
																			});
																			window.location.reload();
																			modal.closeModal(modalId);
																		} else {
																			setError(response.message);
																			toast.error("Invalid password. Please try again.");
																		}
																	}}
																/>
															),
														});
													}}
												>
													<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
														<span>Delete</span>
													</div>
												</DropdownMenuItem>
											</HasPermission>
										</DropdownMenuGroup>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>
					</div>

					<div className="flex flex-col w-full pl-10">
						{items.length > 0 && showReplies && depth < MAX_DEPTH && (
							<div className="flex flex-col w-full">
								{items.map((itemActivity, index) => (
									<CaseActivityChild
										key={index}
										activeStatus={activeStatus}
										title={itemActivity.activity}
										depth={depth + 1}
										activity={itemActivity}
										caseData={caseData}
										isReadOnly={isReadOnly}
										nextStage={nextStage}
									/>
								))}
							</div>
						)}
					</div>
				</div>
			</CaseBlock>
		</div>
	);
};

const CaseActivityChild = ({
	isReadOnly = false,
	title,
	depth,
	caseData,
	activeStatus,
	activity,
	nextStage,
}: { isReadOnly: boolean; title: string; depth: number }) => {
	return (
		<div className="flex flex-col w-full last:border-b-0 border-dashed border-border group">
			{activity.type === "Hearing" && (
				<div className="hidden group-first:flex flex-row gap-2 pb-4 items-center w-full">
					<CaseBlockHead
						customIcon={(isOpen) => (isOpen ? <IconChevronDown size={14} stroke={2} /> : <IconChevronRight size={14} stroke={2} />)}
						inlineIcon={true}
					>
						{(isOpen) => (
							<div className="flex flex-col w-auto">
								<span className="text-sm font-bold text-foreground-dark">{isOpen ? "See Less" : "See More"}</span>
							</div>
						)}
					</CaseBlockHead>
					<div className="border-b w-auto border-dashed border-border" />
				</div>
			)}
			<CaseBlockBody>
				<div className="border-b w-full border-dashed border-border">
					<CaseActivityItem
						isReadOnly={isReadOnly}
						activeStatus={activeStatus}
						activity={activity}
						caseData={caseData}
						title={title}
						depth={depth}
						nextStage={nextStage}
					/>
				</div>
			</CaseBlockBody>
		</div>
	);
};

export const CaseActivity = ({
	isReadOnly = false,
	nextVisibleStage,
	activities,
	activeStatus,
	currentTab,
	nextStage,
	data,
	sortBy = "latest",
	onSortChange,
}: CaseActivitiesProps) => {
	const modal = useModal();
	const caseActivitiesMutation = useCaseActivityScheduleHearingMutation();
	const openHearingModal = () => {
		const modalId = modal.openModal({
			allowClose: true,
			title: "Add Hearing Schedule",
			size: "2xl",
			showHeader: true,
			showFooter: false,
			component: (
				<CaseHearingModal
					stage={currentTab}
					data={activities}
					caseData={data}
					caseClassification={data.case_classification}
					handleSubmit={async (values) => {
						await caseActivitiesMutation.mutateAsync({
							case_id: data.id,
							hearings: values.hearings,
						});
						await queryClient.invalidateQueries({
							queryKey: ["case_activities", "calendars"],
						});
						modal.closeModal(modalId);
					}}
				/>
			),
		});
	};

	const openAddModal = (activityId = null, defaultActivity = null) => {
		const input = {
			stage: activeStatus,
			activity_id: activityId || null,
			activity: defaultActivity,
		};
		modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: (
				<CaseActivityModal
					nextVisibleStage={nextVisibleStage}
					title="Add Activity"
					collaborators={data.collaborators}
					data={input}
					caseRecord={data}
					nextStage={nextStage}
					allowPostAs={data.allow_post_as}
				/>
			),
		});
	};

	const caseRecordStatusMutation = useCaseRecordStatusMutation(data?.id);
	const navigate = useNavigate();
	return (
		<div className="flex flex-col w-full gap-4">
			<div className="flex flex-row justify-between items-center w-full gap-4">
				<span className="font-bold capitalize text-foregtound-dark text-base">{currentTab?.toLowerCase().replace(/_/g, " ")} Activities</span>
				<div className="inline-flex flex-row items-center gap-1">
					{onSortChange && (
						<div className="inline-flex flex-row items-center gap-2 mr-2">
							<span className="text-sm text-foreground/70">Sort:</span>
							<DropdownMenu modal={false}>
								<DropdownMenuTrigger asChild>
									<Button size="sm" variant="outline">
										{sortBy === "latest" ? (
											<>
												<IconSortDescending size={14} stroke={2} />
												<span>Latest</span>
											</>
										) : (
											<>
												<IconSortAscending size={14} stroke={2} />
												<span>Oldest</span>
											</>
										)}
										<IconChevronDown size={14} stroke={2} />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuGroup>
										<DropdownMenuItem onClick={() => onSortChange("latest")} className="cursor-pointer">
											<div className="flex flex-row items-center gap-2">
												<IconSortDescending size={14} stroke={2} />
												<span>Latest First</span>
											</div>
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => onSortChange("oldest")} className="cursor-pointer">
											<div className="flex flex-row items-center gap-2">
												<IconSortAscending size={14} stroke={2} />
												<span>Oldest First</span>
											</div>
										</DropdownMenuItem>
									</DropdownMenuGroup>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					)}
					{!isReadOnly && (
						<HasPermission permissions={[userPermissions.ADD_HEARING]}>
							{data.status !== "case_closed" && (
								<Button
									size="sm"
									variant="green"
									onClick={() => {
										openHearingModal();
									}}
								>
									<IconPlus size={14} stroke={2} />
									<span className="">Add Hearing</span>
								</Button>
							)}
						</HasPermission>
					)}
					{!isReadOnly && data.stage === caseStages.POST_JUDGMENT && (
						<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
							{data.status !== "case_closed" && (
								<Button
									size="sm"
									onClick={() => {
										openAddModal(false, "Writ of Execution");
									}}
								>
									<IconPlus size={14} stroke={2} />
									<span className="">Add Writ of Execution</span>
								</Button>
							)}
						</HasPermission>
					)}
					{!isReadOnly && (
						<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
							<Button
								size="sm"
								onClick={() => {
									openAddModal(false);
								}}
							>
								<IconPlus size={14} stroke={2} />
								<span className="">Add Activity</span>
							</Button>
						</HasPermission>
					)}
					{!isReadOnly && (
						<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
							<Button
								size="sm"
								onClick={() => {
									openAddModal(false, "Sheriff's Report / Process Server's Report");
								}}
							>
								<IconPlus size={14} stroke={2} />
								<span className="">Add Sheriff's Report</span>
							</Button>
						</HasPermission>
					)}
					{!isReadOnly && (
						<HasPermission permissions={[userPermissions.ADD_ACTIVITY]}>
							{(data.stage === caseStages.POST_JUDGMENT || data.stage === caseStages.APPEAL) && (
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button size="sm" variant="default">
											<span className="">Set As</span>
											<IconChevronDown size={18} stroke={2} />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="start" sideOffset={8}>
										<DropdownMenuGroup>
											<DropdownMenuItem
												className="cursor-pointer"
												onClick={() => {
													modal.openModal({
														title: "Close Case",
														description: "Are you sure you want to close this case? You cannot undo this action once confirmed.",
														type: "confirm",
														variant: "green",
														onConfirm: async () => {
															await caseRecordStatusMutation.mutateAsync({
																id: data.id,
																status: "case_closed",
																stage: nextStage?.name,
															});

															await navigate({
																to: `/case-evaluation/${data.id}`,
																search: (old) => ({
																	...old,
																	tabs: "case-flow",
																	stage: "POST_JUDGMENT",
																}),
															});
														},
													});
												}}
											>
												<div className="flex flex-row items-center w-full py-1 px-2">Case Closed</div>
											</DropdownMenuItem>
											<DropdownMenuItem
												className="cursor-pointer"
												onClick={() => {
													const modalId = modal.openModal({
														allowClose: false,
														size: "full",
														showHeader: false,
														showFooter: false,
														component: (
															<CaseAppealModal
																caseRecord={data}
																data={data}
																title="APPEAL"
																handleSubmit={async (values) => {
																	try {
																		await caseRecordStatusMutation.mutateAsync(values);
																		await navigate({
																			to: `/case-evaluation/${data.id}`,
																			search: (old) => ({
																				...old,
																				tabs: "case-flow",
																				stage: values.move_back,
																			}),
																		});
																		modal.closeModal(modalId);
																	} catch (serror) {}
																}}
															/>
														),
													});
												}}
											>
												<div className="flex flex-row items-center w-full py-1 px-2">With Appeal</div>
											</DropdownMenuItem>
											{!data?.archived_at ? (
												<DropdownMenuItem
													className="cursor-pointer"
													onClick={() => {
														modal.openModal({
															title: "Archive Case",
															description: "Are you sure you want to archive this case? You cannot undo this action once confirmed.",
															type: "confirm",
															variant: "green",
															onConfirm: async () => {
																await caseRecordStatusMutation.mutateAsync({
																	id: data.id,
																	status: "archived",
																	stage: nextStage?.name,
																});

																await navigate({
																	to: `/case-evaluation/${data.id}`,
																	search: (old) => ({
																		...old,
																		tabs: "case-flow",
																		stage: "POST_JUDGMENT",
																	}),
																});
															},
														});
													}}
												>
													<div className="flex flex-row items-center w-full py-1 px-2">Archive</div>
												</DropdownMenuItem>
											) : (
												<DropdownMenuItem
													className="cursor-pointer"
													onClick={() => {
														modal.openModal({
															title: "Revive Case",
															description: "Are you sure you want to archive this case? You cannot undo this action once confirmed.",
															type: "confirm",
															variant: "green",
															onConfirm: async () => {
																await caseRecordStatusMutation.mutateAsync({
																	id: data.id,
																	status: "revived",
																	stage: nextStage?.name,
																});

																await navigate({
																	to: `/case-evaluation/${data.id}`,
																	search: (old) => ({
																		...old,
																		tabs: "case-flow",
																		stage: "POST_JUDGMENT",
																	}),
																});
															},
														});
													}}
												>
													<div className="flex flex-row items-center w-full py-1 px-2">Revive</div>
												</DropdownMenuItem>
											)}
										</DropdownMenuGroup>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</HasPermission>
					)}
				</div>
			</div>
			{activities.length ? (
				<div className="flex flex-col w-full pl-2">
					<div className="flex flex-col w-full pl-6 gap-6 border-l border-border/50">
						<div className="relative flex flex-col w-full gap-2">
							<div className="absolute right-full -top-1.5 mr-2 inline-flex w-[32px] h-[32px] rounded-full justify-center items-center bg-background text-primary-600">
								<IconCircleDot size={18} stroke={2} />
							</div>
							<div className="flex flex-row items-center w-full gap-2">
								<span className="text-base font-semibold text-foreground/70"> </span>
							</div>
							<div className="flex flex-col w-full rounded-md border border-border/50">
								{activities.map((activity, index) => (
									<CaseActivityItem
										key={index}
										activity={activity}
										activeStatus={activeStatus}
										caseData={data}
										nextStage={nextStage}
										isReadOnly={isReadOnly}
										title={activity.activity}
										items={activity.replies || []}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className="flex flex-col w-full rounded-md border border-border/50">
					<Blanket width="sm">
						<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
						<BlanketInfo title="No Activities" message="No activities recorded yet. Start by adding a hearing or an activity." />
					</Blanket>
				</div>
			)}
		</div>
	);
};
